import Breadcrumb, { getSlugsByContent } from "@/components/Breadcrumb";
import ConditionalSkeleton from "@/components/ConditionalSkeleton";
import { Flex, FlexRowAlignCenter } from "@/components/Flex";
import { useEditorContextSelector } from "@/components/FullPageEditor/hooks/useEditorContextSelector";
import { editorThemeColors } from "@/components/FullPageEditor/utils/editorThemeColors";
import { LastSavedTime } from "@/components/LastSavedTime";
import { useNotePageContextSelector } from "@/components/NotePage/hooks/NotePageContext";
import { useSidebarContextSelector } from "@/components/NotePage/hooks/SidebarContext";
import NoteOptionsMenu from "@/components/cards/UserContentCard/components/NoteOptionsMenu";
import UserAvatar from "@/components/userCardComponents/UserAvatar";
import Link from "@/components/wrappers/Link";
import { useMounted } from "@/hooks/useMounted";
import { TIME } from "@/utils/dateTimeUtils";
import { iconSizes } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { Note } from "@knowt/syncing/graphql/schema";
import { useClass } from "@knowt/syncing/hooks/classes/useClass";
import Box from "@mui/material/Box";
import { RefreshCcw } from "lucide-react";
import { useUpdate } from "react-use";
import useInterval from "react-use/lib/useInterval";
import FullScreenButton from "../FullScreenButton";
import NavbarCreateContentBtn from "@/features/NavBar/components/NavbarCreateContentBtn";
import { useFolderAncestors } from "@knowt/syncing/hooks/folders/useFolders";

const NoteHeaderBreadcrumb = ({ note }: { note: Note }) => {
    const { course } = useClass({ classId: note?.classId });
    const { ancestors } = useFolderAncestors({ folderId: note?.folderId });

    const slugs = note?.classId
        ? [
              {
                  label: course?.name as string,
                  href: `/class/${course?.classId}/dashboard`,
                  img: `/images/class-icon.svg`,
              },
          ]
        : getSlugsByContent({ note });

    const breadcrumbItems =
        slugs.length > 0
            ? slugs
            : ([
                  ...(ancestors?.map(ancestorFolder => ({
                      label: ancestorFolder.name,
                      href: `/folder/${ancestorFolder.folderId}`,
                  })) ?? []),
                  { label: note.title },
              ] as {
                  label: string;
                  href?: string;
                  img?: string;
              }[]);

    return <Breadcrumb root="home" items={breadcrumbItems} />;
};

const LastSaved = () => {
    const note = useNotePageContextSelector(state => state.note);
    const isSaving = useEditorContextSelector(state => state.isSaving);

    useInterval(useUpdate(), TIME.SECOND * 10);

    const mounted = useMounted();
    if (!mounted) return null;

    return (
        <FlexRowAlignCenter style={{ columnGap: spacing.XS_2 }}>
            {isSaving && (
                <Box
                    sx={{
                        animation: "spin 2s linear infinite",
                        "@keyframes spin": {
                            "0%": { transform: "rotate(360deg)" },
                            "100%": { transform: "rotate(0deg)" },
                        },
                    }}>
                    <RefreshCcw size={iconSizes.SM} color={"#CECECE"} />
                </Box>
            )}
            <LastSavedTime timestamp={+(note.updated ?? 0) * 1000} style={{ fontWeight: "600" }} />
        </FlexRowAlignCenter>
    );
};

const Header = ({ style }: { style?: React.CSSProperties }) => {
    const note = useNotePageContextSelector(state => state.note);
    const initialNote = useNotePageContextSelector(state => state.initialNote);
    const ownerData = useNotePageContextSelector(state => state.ownerData);
    const nonEditable = useNotePageContextSelector(state => state.nonEditable);
    const loading = useNotePageContextSelector(state => state.loading);

    const toggleNoteSidebar = useSidebarContextSelector(s => s.toggleNoteSidebar);

    const renderLeftHeader = () => (
        <FlexRowAlignCenter className="hide-scrollbar" style={{ gap: "1.4rem", overflowX: "auto", flex: 1 }}>
            <FullScreenButton onClick={toggleNoteSidebar} />
            <NoteHeaderBreadcrumb note={note || initialNote} />
        </FlexRowAlignCenter>
    );

    const renderRightHeader = () => (
        <FlexRowAlignCenter style={{ gap: "1.4rem", alignSelf: "flex-end" }}>
            <LastSaved />
            <NavbarCreateContentBtn />
            <NoteOptionsMenu note={note} isNotePageMenu btnSx={{ width: "4rem", height: "4rem" }} />
            <ConditionalSkeleton condition={loading} variant="circular" animation={"wave"}>
                {nonEditable && ownerData ? (
                    <Link href={`/u/${ownerData.username}`}>
                        <UserAvatar
                            style={{ cursor: "pointer" }}
                            radius="4rem"
                            src={ownerData?.pictureUrl || ""}
                            alt={ownerData?.Name || ownerData?.username || "U"}
                        />
                    </Link>
                ) : null}
            </ConditionalSkeleton>
        </FlexRowAlignCenter>
    );

    return (
        <Flex
            style={{
                justifyContent: "space-between",
                backgroundColor: editorThemeColors.background,
                gap: "2rem",
                zIndex: 4,
                padding: "1.5rem 2rem 2rem",
                position: "sticky",
                flexShrink: 0,
                top: 0,
                ...style,
            }}>
            {renderLeftHeader()}
            {renderRightHeader()}
        </Flex>
    );
};

export default Header;
